/**
 * RBAC admin API client — shared by the Users and Roles system features.
 *
 * Query keys live under ["rbac", ...]; every mutation invalidates the whole
 * family plus ["me"] so editing your own grants refreshes the sidebar
 * immediately (AuthProvider caches /me for 5 minutes otherwise).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export interface PermissionRow {
  id: string;
  key: string;
  description: string;
  feature: string;
  action: string;
}

export type CategoryAccess = "read" | "write";

export interface CategoryGrant {
  category: string;
  access: CategoryAccess;
}

export interface RoleRow {
  id: string;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
  permissions: PermissionRow[];
  category_grants: CategoryGrant[];
  member_count: number;
}

export interface UserRow {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
  roles: RoleRow[];
  direct_permissions: PermissionRow[];
}

export interface CategoryInfo {
  name: string;
  /** Feature keys whose permissions this category covers (live, from the
   *  backend registry — the same map `expand_category_grants` enforces). */
  features: string[];
}

export function useUsers() {
  return useQuery({
    queryKey: ["rbac", "users"],
    queryFn: () => apiFetch<UserRow[]>("/users"),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["rbac", "roles"],
    queryFn: () => apiFetch<RoleRow[]>("/roles"),
  });
}

export function usePermissions() {
  return useQuery({
    queryKey: ["rbac", "permissions"],
    queryFn: () => apiFetch<PermissionRow[]>("/permissions"),
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ["rbac", "categories"],
    queryFn: async () => {
      const out = await apiFetch<{ categories: CategoryInfo[] }>(
        "/permissions/categories",
      );
      return out.categories;
    },
    staleTime: Infinity, // static config — changes only with a backend deploy
  });
}

function useRbacMutation<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rbac"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export interface RoleWriteBody {
  name?: string;
  description?: string;
  permission_ids?: string[];
  category_grants?: CategoryGrant[];
}

export function useCreateRole() {
  return useRbacMutation(
    async (body: RoleWriteBody & { key: string; name: string }) =>
      apiFetch<RoleRow>("/roles", { method: "POST", body }),
  );
}

export function useUpdateRole() {
  return useRbacMutation(async ({ roleId, body }: { roleId: string; body: RoleWriteBody }) =>
    apiFetch<RoleRow>(`/roles/${roleId}`, { method: "PATCH", body }),
  );
}

export function useUpdateUser() {
  return useRbacMutation(
    async ({ userId, body }: { userId: string; body: { name?: string; is_active?: boolean } }) =>
      apiFetch<UserRow>(`/users/${userId}`, { method: "PATCH", body }),
  );
}

export function useGrantRole() {
  return useRbacMutation(async ({ userId, roleId }: { userId: string; roleId: string }) =>
    apiFetch<void>(`/users/${userId}/roles/${roleId}`, { method: "POST" }),
  );
}

export function useRevokeRole() {
  return useRbacMutation(async ({ userId, roleId }: { userId: string; roleId: string }) =>
    apiFetch<void>(`/users/${userId}/roles/${roleId}`, { method: "DELETE" }),
  );
}

export function useGrantUserPermission() {
  return useRbacMutation(async ({ userId, permId }: { userId: string; permId: string }) =>
    apiFetch<void>(`/users/${userId}/permissions/${permId}`, { method: "POST" }),
  );
}

export function useRevokeUserPermission() {
  return useRbacMutation(async ({ userId, permId }: { userId: string; permId: string }) =>
    apiFetch<void>(`/users/${userId}/permissions/${permId}`, { method: "DELETE" }),
  );
}
